'use client';

/**
 * StopList — the trip as a lettered timeline, in real days. The planner's
 * main surface.
 *
 * ┌ Day 1  Sat 17 Oct ─────────── [tonight's town]  27°  ☀ 7:41 pm ┐
 *   Melbourne → Lakes Entrance
 *   319 km · 3h 50m · arrive 12:50 pm
 * ⋮⋮ (A)  Melbourne                              [photo]  ⋮
 *         Start · leave 9:00 am
 *     ├── [car 280 km · 4h  ↗]                    ← the leg, on the connector
 *     │   [✦ Start at Port Fairy saves 240 km  (Apply)  ×]
 * ⋮⋮ (B)  Lakes Entrance                         [photo]  ⋮
 *         Overnight  [−  1 night  +] · arrive 12:50 pm
 *         [⛺ No camp yet · Sat 17 Oct        Find a camp]
 * ┌ Day 2  Sun 18 Oct ─────────────────────────────────────────────┐
 *     ○   Leave Lakes Entrance                9:00 am
 *
 * Days come from segmentIntoDays: a day ends wherever you sleep, and a run
 * of pass-throughs past the daily drive budget splits too. Each day opens on
 * a band in its colour (the same as its pins and route on the map), with
 * tonight's town, sunset and the top temperature once the trip has a date.
 * Rest days at a multi-night stop get their own band. Day chips at the top
 * (DayNav) follow the scroll once there are two or more days.
 *
 * One row per stop: the name gets its own line, the role, nights and clock
 * sit under it. The drive to the next stop sits ON the connector between the
 * two dots, so it reads as the leg. A day with one drive says it once, in
 * its header. One control per row: a 44px options menu (move earlier/later,
 * move to another day, camps near here, directions, note, rename, remove). A
 * six-dot grip shows on hover (always on touch screens): drag it, or press
 * and drag anywhere on the row with a mouse, or long-press the row on touch.
 * Alt+Up/Down on the name moves a stop from the keyboard. Reorders animate:
 * rows glide to their new place (FLIP), unless reduced motion is on.
 *
 * The letters are the map's (planner/roles.ts): "C" in the list is "C" on
 * the map, in the same colour (the day's, once the trip is in days).
 */
import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { LadderLeg, TripStop } from './types';
import { MAX_NIGHTS_PER_STOP, roleLabel } from '../../utils/tripRoles';
import { ROLE_BADGE, roleAt, stopLetter } from './roles';
import { findBacktrack, formatDrive, formatKm, hasCoords, moveStopTo, optimisationSaving } from './geo';
import { dateForTripDay, segmentIntoDays, stopTimes, type TripDay } from '../../utils/tripPlanner';
import { formatClock, formatShortDate } from '../../utils/tripFormat';
import { sunsetLabel } from '../../utils/sunTimes';
import { dayColor, stopDayIndices } from '../../utils/tripDayColors';
import { heroPhotoUrl, type StopPhotoMap } from './stopPhotos';
import {
  IconArrowDown,
  IconArrowUp,
  IconCalendar,
  IconCar,
  IconCompass,
  IconGrip,
  IconPin,
  IconMinus,
  IconMoon,
  IconMoreVertical,
  IconNote,
  IconPencil,
  IconPlus,
  IconRoute,
  IconTent,
  IconX,
} from './icons';
import { Button, FIELD_CLASS, Menu, type MenuItem } from './ui';
import { AddStopBetweenPanel, prefetchLegIdeas } from './AddStopBetween';
import type { ResolvedLocation } from '../LocationAutocomplete';
import { placementInfo } from '../../utils/tripInsert';
import DayHeader, { RestDayHeader, dayActionBtn, isLongDay } from './DayHeader';
import DayNav, { type DayNavItem } from './DayNav';
import { dayOfStop, moveStopToDay, moveToDayTargets, splitDayPlan, useDayForecasts } from './itinerary';

export interface StopListProps {
  stops: TripStop[];
  legs: LadderLeg[];
  /** Straight-line estimates (guests). Said once in the header, not per leg. */
  estimate: boolean;
  onReorder: (stops: TripStop[]) => void;
  onRemoveStop: (stopId: string) => void;
  onEditNote: (stopId: string, notes: string) => void;
  onEditName: (stopId: string, name: string) => void;
  onChangeNights?: (stopId: string, nights: number) => void;
  /** Re-order to the shortest drive (start and end kept). */
  onOptimise?: (order: TripStop[], savedKm: number) => void;
  /** 64px photo per stop (see stopPhotos.ts). undefined = loading, null = none. */
  photos?: StopPhotoMap;
  /** Departure date (yyyy-mm-dd). Day dividers carry the date once it is set. */
  startDate?: string | null;
  /**
   * Present → every leg gets a "+" on the timeline: it opens a search right
   * there, and the pick lands at exactly that position (index = where the new
   * stop goes, 1..n-1).
   */
  onInsertStop?: (place: ResolvedLocation, index: number) => void;
  /** The drawn road line: insert-box rows say how far off it they are. */
  route?: { lat: number; lng: number }[] | null;
  /** A stop that just landed: its row glows and its letter pulses once. */
  highlightStopId?: string | null;
  /** Controlled: the leg (index of the stop above) whose "+" box is open. */
  insertOpenAt?: number | null;
  onInsertOpenAtChange?: (index: number | null) => void;
  /**
   * The daily drive budget (minutes). A run of pass-throughs longer than this
   * splits into days, and a day over it says "Long day". Without it, days
   * split at nights only and "Long day" means over 8 hours.
   */
  maxDriveMinutesPerDay?: number;
  /** "Find camps near here" in a stop's menu. */
  onFindCamps?: (stop: TripStop) => void;
  /** "No camp yet · Find a camp" under each overnight without a campsite. */
  onAddCamp?: (stop: TripStop) => void;
  /** "Explore nearby" on a rest day. */
  onExploreNear?: (stop: TripStop) => void;
  /** The stop picked here or on the map: its row is tinted, its pin grows. */
  selectedStopId?: string | null;
  /** The stop under the pointer here or on the map. */
  hoveredStopId?: string | null;
  /** The letter badge was pressed: show this stop on the map. */
  onSelectStop?: (stopId: string) => void;
  /** Pointer entered (id) or left (null) a stop's row. */
  onHoverStop?: (stopId: string | null) => void;
  /** Pointer entered a leg (by the stop it drives to) or left it (null). */
  onHoverLeg?: (toStopId: string | null) => void;
  /** Inside a leg's "+" box, under its ideas: every camp on that drive. */
  renderLegCamps?: (index: number) => React.ReactNode;
  /** Present → a stop's photo opens it up close (photo, write-up, next leg). */
  onOpenStop?: (stopId: string) => void;
}

function directionsUrl(s: TripStop): string | null {
  if (!hasCoords(s)) return null;
  return `https://www.google.com/maps/dir/?api=1&destination=${s.lat},${s.lng}`;
}

/** Google Maps directions for one drive (origin to destination). */
export function legDirectionsUrl(from: TripStop, to: TripStop): string | null {
  if (!hasCoords(from) || !hasCoords(to)) return null;
  return `https://www.google.com/maps/dir/?api=1&origin=${from.lat},${from.lng}&destination=${to.lat},${to.lng}&travelmode=driving`;
}

/** "266 km · 3h 48m", with "approx." only on an estimate. */
export function legLabel(leg: Pick<LadderLeg, 'distanceKm' | 'driveMinutes'> | null | undefined, estimate: boolean): string {
  const figures = [formatKm(leg?.distanceKm), formatDrive(leg?.driveMinutes)].filter(Boolean).join(' · ');
  if (!figures) return '';
  return estimate ? `${figures} approx.` : figures;
}

/** The small badge on a stop's photo: what kind of night it is. */
export function stopBadge(stop: Pick<TripStop, 'kind' | 'tags'>): string | null {
  const tags = (stop.tags || []).map((t) => t.toLowerCase());
  if (tags.includes('free')) return 'Free';
  if (tags.includes('powered')) return 'Powered';
  if (stop.kind === 'camp') return 'Camp';
  return null;
}

/** Without a drive budget, a day over this much driving is a "Long day". */
export const LONG_DAY_MINUTES = 8 * 60;

/**
 * The trip in driving days, and whether they're worth headings: once the
 * trip has a date, or more than one day.
 */
export function itineraryDays(
  stops: TripStop[],
  legs: LadderLeg[],
  maxDriveMinutesPerDay?: number,
  startDate?: string | null
): { days: TripDay[]; show: boolean } {
  if (stops.length < 2) return { days: [], show: false };
  const days = segmentIntoDays(stops, legs, maxDriveMinutesPerDay ?? Number.POSITIVE_INFINITY);
  return { days, show: days.length > 0 && (!!startDate || days.length >= 2) };
}

export default function StopList({
  stops,
  legs,
  estimate,
  onReorder,
  onRemoveStop,
  onEditNote,
  onEditName,
  onChangeNights,
  onOptimise,
  photos,
  startDate,
  onInsertStop,
  route = null,
  highlightStopId = null,
  insertOpenAt: insertOpenAtProp,
  onInsertOpenAtChange,
  onOpenStop,
  maxDriveMinutesPerDay,
  onFindCamps,
  onAddCamp,
  onExploreNear,
  selectedStopId = null,
  hoveredStopId = null,
  onSelectStop,
  onHoverStop,
  onHoverLeg,
  renderLegCamps,
}: StopListProps) {
  const uid = useId();
  // The leg whose inline "add a stop here" box is open (index of the stop
  // above). Controlled when the host passes insertOpenAt (it greys out the
  // top box so only one search field is live at a time).
  const [insertOpenLocal, setInsertOpenLocal] = useState<number | null>(null);
  const insertControlled = insertOpenAtProp !== undefined;
  const insertOpenAt = insertControlled ? insertOpenAtProp : insertOpenLocal;
  const insertChangeRef = useRef(onInsertOpenAtChange);
  insertChangeRef.current = onInsertOpenAtChange;
  const setInsertOpenAt = useCallback(
    (v: number | null) => {
      if (!insertControlled) setInsertOpenLocal(v);
      insertChangeRef.current?.(v);
    },
    [insertControlled]
  );
  // The list changed under an open box (a stop added/removed): close it rather
  // than leave it pointing at the wrong pair.
  const stopIdsKey = stops.map((s) => s.id).join('|');
  const firstKeyRef = useRef(stopIdsKey);
  useEffect(() => {
    if (firstKeyRef.current === stopIdsKey) return;
    firstKeyRef.current = stopIdsKey;
    setInsertOpenLocal(null);
    insertChangeRef.current?.(null);
  }, [stopIdsKey]);
  const onTripIds = useMemo(
    () => stops.map((s) => s.entityId).filter((n): n is number => typeof n === 'number'),
    [stops]
  );
  // Warm each leg's "On this drive" ideas once the trip settles, so a "+"
  // opens with them in place.
  const routeWarmKey = route && route.length >= 2 ? `${route.length}:${route[0].lat.toFixed(3)}` : '';
  useEffect(() => {
    if (!onInsertStop || stops.length < 2) return;
    const id = window.setTimeout(() => prefetchLegIdeas(stops, route), 1200);
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stopIdsKey, routeWarmKey]);
  const [editingNameId, setEditingNameId] = useState<string | null>(null);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [drag, setDrag] = useState<{ from: number; to: number } | null>(null);
  const dragRef = useRef<{ from: number; to: number } | null>(null);
  const rowRefs = useRef<(HTMLLIElement | null)[]>([]);
  const listRef = useRef<HTMLOListElement>(null);
  const longPress = useRef<{ timer: ReturnType<typeof setTimeout> | null; x: number; y: number; index: number } | null>(
    null
  );
  // Mouse: a press on a row becomes a drag once it moves 5px.
  const pressRef = useRef<{ x: number; y: number; index: number } | null>(null);
  const suppressClick = useRef(false);
  const [announce, setAnnounce] = useState('');
  const [dismissedFix, setDismissedFix] = useState<string | null>(null);

  const legByOrigin = useMemo(() => {
    const m = new Map<string, LadderLeg>();
    legs.forEach((l) => m.set(l.fromId, l));
    return m;
  }, [legs]);

  // A leg that doubles back is only worth flagging when there is a better
  // order to offer: the same ends with the middle reordered, or — when the
  // first leg drives away from the destination — starting at the far end.
  // An out-and-back with no better order is deliberate, so it stays quiet.
  // The hint rides on the leg itself, one line, and can be dismissed.
  const backtrack = useMemo(() => findBacktrack(stops), [stops]);
  const fix = useMemo(() => {
    if (!backtrack) return null;
    const middle = optimisationSaving(stops);
    if (middle) return { ...middle, newStart: null as TripStop | null };
    const start = optimisationSaving(stops, 10, { keepStart: false });
    return start ? { ...start, newStart: start.order[0] } : null;
  }, [backtrack, stops]);
  const orderKey = stops.map((s) => s.id).join('>');
  const showFix = !!(backtrack && fix && dismissedFix !== orderKey);

  // Days: a heading per driving day once the trip has a date or more than
  // one day. Each heading sits straight after the stop where the day before
  // ended, so a day owns its own driving.
  const { days, show: showDays } = useMemo(
    () => itineraryDays(stops, legs, maxDriveMinutesPerDay, startDate),
    [stops, legs, maxDriveMinutesPerDay, startDate]
  );
  const times = useMemo(() => stopTimes(stops, legs, days), [stops, legs, days]);
  const stopDayIdx = useMemo(() => stopDayIndices(stops.length, days), [stops.length, days]);
  const dayStartingAt = useMemo(() => {
    const m = new Map<number, TripDay>();
    days.forEach((d) => {
      if (d.index > 0) m.set(d.firstStopIndex, d);
    });
    return m;
  }, [days]);
  const longOver = maxDriveMinutesPerDay ?? LONG_DAY_MINUTES;
  const forecasts = useDayForecasts(showDays ? days : [], stops, startDate);
  const dayId = (d: Pick<TripDay, 'index'>) => `${uid}-day-${d.index}`;
  const restId = (d: Pick<TripDay, 'index'>, r: number) => `${uid}-rest-${d.index}-${r}`;
  const navItems = useMemo((): DayNavItem[] => {
    if (!showDays) return [];
    const out: DayNavItem[] = [];
    days.forEach((d) => {
      const restStop = stops[d.firstStopIndex];
      for (let r = d.restDaysBefore; r >= 1 && d.index > 0; r -= 1) {
        out.push({
          id: restId(d, r),
          dayNumber: d.dayOffset - r + 1,
          description: `Rest day in ${restStop?.name ?? ''}`,
          place: restStop?.name,
          date: dateForTripDay(startDate, d.dayOffset - r),
          color: dayColor(stopDayIdx[d.firstStopIndex] ?? 0),
          rest: true,
        });
      }
      const tonight = stops[d.lastStopIndex];
      out.push({
        id: dayId(d),
        dayNumber: d.dayOffset + 1,
        description: `${stops[d.firstStopIndex]?.name ?? ''} to ${tonight?.name ?? ''}`,
        place: tonight?.name,
        date: dateForTripDay(startDate, d.dayOffset),
        color: dayColor(d.index),
      });
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDays, days, stops, startDate, stopDayIdx, uid]);

  // ── Motion: rows glide to their new place after any reorder (FLIP) ───────
  const rowById = useRef(new Map<string, HTMLLIElement>());

  // A stop picked on the map scrolls its row into view.
  useEffect(() => {
    if (!selectedStopId) return;
    const el = rowById.current.get(selectedStopId);
    const reduce =
      typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    el?.scrollIntoView?.({ block: 'nearest', behavior: reduce ? 'auto' : 'smooth' });
  }, [selectedStopId]);
  const lastTops = useRef(new Map<string, number>());
  useLayoutEffect(() => {
    const reduce =
      typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const next = new Map<string, number>();
    rowById.current.forEach((el, id) => {
      if (!el.isConnected) {
        rowById.current.delete(id);
        return;
      }
      const top = el.offsetTop;
      next.set(id, top);
      const prev = lastTops.current.get(id);
      if (reduce || prev === undefined || typeof el.animate !== 'function') return;
      const dy = prev - top;
      if (Math.abs(dy) < 2) return;
      el.animate([{ transform: `translateY(${dy}px)` }, { transform: 'translateY(0)' }], {
        duration: 320,
        easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
      });
    });
    lastTops.current = next;
  }, [orderKey]);

  // ── Drag: one model for mouse (press the row) and touch (long-press) ─────
  const targetIndexAt = useCallback((clientY: number): number => {
    const rows = rowRefs.current;
    for (let i = 0; i < rows.length; i += 1) {
      const r = rows[i]?.getBoundingClientRect();
      if (r && clientY < r.top + r.height / 2) return i;
    }
    return rows.length - 1;
  }, []);

  const setDragState = (next: { from: number; to: number } | null) => {
    dragRef.current = next;
    setDrag(next);
  };

  const finishDrag = useCallback(() => {
    const d = dragRef.current;
    setDragState(null);
    if (!d) return;
    // The pointerup that ends a drag must not also click the name (rename).
    suppressClick.current = true;
    window.setTimeout(() => {
      suppressClick.current = false;
    }, 0);
    if (d.from === d.to) return;
    const moved = stops[d.from];
    onReorder(moveStopTo(stops, d.from, d.to));
    if (moved) setAnnounce(`${moved.name} moved to position ${d.to + 1} of ${stops.length}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stops, onReorder]);

  // Mouse/pen: press anywhere on a row (not on a control), move 5px → drag.
  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      const p = pressRef.current;
      if (p && !dragRef.current && Math.hypot(e.clientX - p.x, e.clientY - p.y) > 5) {
        pressRef.current = null;
        setDragState({ from: p.index, to: p.index });
      }
      const d = dragRef.current;
      if (!d) return;
      const to = targetIndexAt(e.clientY);
      if (to !== d.to) setDragState({ from: d.from, to });
    };
    const onUp = () => {
      pressRef.current = null;
      if (dragRef.current) finishDrag();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && dragRef.current) setDragState(null);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('keydown', onKey);
    };
  }, [finishDrag, targetIndexAt]);

  // Touch: long-press a row to pick it up. A non-passive touchmove stops the
  // page scrolling only while a stop is held, so normal scrolling still works.
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const onTouchMove = (e: TouchEvent) => {
      const t = e.touches[0];
      const lp = longPress.current;
      if (lp?.timer && t && Math.hypot(t.clientX - lp.x, t.clientY - lp.y) > 8) {
        clearTimeout(lp.timer);
        longPress.current = null;
      }
      const d = dragRef.current;
      if (!d || !t) return;
      e.preventDefault();
      const to = targetIndexAt(t.clientY);
      if (to !== d.to) setDragState({ from: d.from, to });
    };
    const onTouchEnd = () => {
      if (longPress.current?.timer) clearTimeout(longPress.current.timer);
      longPress.current = null;
      if (dragRef.current) finishDrag();
    };
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd);
    el.addEventListener('touchcancel', onTouchEnd);
    return () => {
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
      el.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [finishDrag, targetIndexAt]);

  const canDrag = (index: number) =>
    stops.length > 1 && !stops[index]?.locked && !editingNameId && !editingNoteId;

  // Controls (menu, stepper, fields, links) never start a drag. The name does
  // (it is most of the row); a press without movement still renames.
  const startsOnControl = (target: EventTarget | null) =>
    !!(target as HTMLElement | null)?.closest?.('input, a, [role="menu"], [data-no-drag], [aria-haspopup]');

  const onRowTouchStart = (e: React.TouchEvent, index: number) => {
    if (!canDrag(index) || startsOnControl(e.target)) return;
    const t = e.touches[0];
    if (!t) return;
    // The grip picks the stop up straight away; the rest of the row needs a
    // long-press so a swipe still scrolls the list.
    if ((e.target as HTMLElement | null)?.closest?.('[data-grip]')) {
      setDragState({ from: index, to: index });
      return;
    }
    const timer = setTimeout(() => {
      longPress.current = null;
      setDragState({ from: index, to: index });
      try {
        navigator.vibrate?.(12);
      } catch {
        /* not supported */
      }
    }, 420);
    longPress.current = { timer, x: t.clientX, y: t.clientY, index };
  };

  const onRowPointerDown = (e: React.PointerEvent, index: number) => {
    if (e.pointerType === 'touch' || e.button !== 0 || !canDrag(index) || startsOnControl(e.target)) return;
    pressRef.current = { x: e.clientX, y: e.clientY, index };
  };

  const move = (index: number, dir: -1 | 1) => {
    const to = index + dir;
    if (to < 0 || to >= stops.length) return;
    const moved = stops[index];
    if (moved?.locked) return;
    onReorder(moveStopTo(stops, index, to));
    if (moved) setAnnounce(`${moved.name} moved to position ${to + 1} of ${stops.length}`);
  };

  /** "Split this day": stay at a stop the day passes through, or open the
   *  "+" box on its longest drive. */
  const splitDay = (d: TripDay): { run: () => void; hint: string } | null => {
    const plan = splitDayPlan(stops, legs, d);
    if (!plan) return null;
    const target = stops[plan.index];
    if (!target) return null;
    if (plan.kind === 'overnight') {
      if (!onChangeNights) return null;
      return {
        hint: `Stay the night at ${target.name.split(',')[0]}`,
        run: () => {
          onChangeNights(target.id, 1);
          setAnnounce(`${target.name} is now an overnight stop`);
        },
      };
    }
    if (!onInsertStop) return null;
    const from = stops[plan.index - 1]?.name.split(',')[0] ?? '';
    return {
      hint: `Add an overnight between ${from} and ${target.name.split(',')[0]}`,
      run: () => {
        setInsertOpenAt(plan.index - 1);
        requestAnimationFrame(() =>
          listRef.current
            ?.querySelector<HTMLElement>(`[data-testid="add-stop-between-panel-${plan.index - 1}"]`)
            ?.scrollIntoView({ block: 'center', behavior: 'smooth' })
        );
      },
    };
  };

  const bandPhoto = (stop: TripStop | undefined) => {
    const url = stop && photos ? photos[stop.id] : null;
    return url ? heroPhotoUrl(url) : null;
  };

  /** One driving day's heading: band, route, figures, long-day fix, weather. */
  const renderDay = (d: TripDay) => {
    const tonight = stops[d.lastStopIndex];
    const date = dateForTripDay(startDate, d.dayOffset);
    const split = isLongDay(d.driveMinutes, longOver) ? splitDay(d) : null;
    return (
      <DayHeader
        titleId={dayId(d)}
        dayNumber={d.dayOffset + 1}
        date={date}
        fromName={stops[d.firstStopIndex]?.name ?? ''}
        toName={tonight?.name ?? ''}
        driveMinutes={d.driveMinutes}
        distanceKm={d.distanceKm}
        arriveMinutes={d.arriveMinutes}
        estimated={d.estimated}
        forecast={forecasts[d.index] ?? null}
        longDayOverMinutes={longOver}
        onSplitDay={split?.run}
        splitHint={split?.hint}
        color={dayColor(d.index)}
        photo={bandPhoto(tonight)}
        sunset={date && tonight ? sunsetLabel(tonight.lat, tonight.lng, date) : null}
      />
    );
  };

  /** The whole days spent at a multi-night stop before day `d` drives on. */
  const renderRestDays = (d: TripDay) => {
    const restStop = stops[d.firstStopIndex];
    if (!restStop || d.restDaysBefore < 1) return null;
    const out: React.ReactNode[] = [];
    for (let r = d.restDaysBefore; r >= 1; r -= 1) {
      const date = dateForTripDay(startDate, d.dayOffset - r);
      out.push(
        <div key={`rest-${r}`} className="pb-4">
          <RestDayHeader
            titleId={restId(d, r)}
            dayNumber={d.dayOffset - r + 1}
            date={date}
            placeName={restStop.name}
            photo={bandPhoto(restStop)}
            color={dayColor(stopDayIdx[d.firstStopIndex] ?? 0)}
            sunset={date ? sunsetLabel(restStop.lat, restStop.lng, date) : null}
            onExplore={onExploreNear && hasCoords(restStop) ? () => onExploreNear(restStop) : undefined}
            onDriveOn={
              onChangeNights && (restStop.nights ?? 0) > 1
                ? () => onChangeNights(restStop.id, (restStop.nights ?? 1) - 1)
                : undefined
            }
          />
        </div>
      );
    }
    return out;
  };

  /** The night(s) an empty stay slot is for: "Sat 17 Oct", "From Sat 17 Oct"
   *  for a longer stay, or without a date "Tonight" / "2 nights". */
  const stayLine = (stop: TripStop, index: number) => {
    const n = stop.nights ?? 1;
    const day = days.find((d) => d.lastStopIndex === index);
    const date = day ? formatShortDate(dateForTripDay(startDate, day.dayOffset)) : null;
    if (date) return n > 1 ? `From ${date}` : date;
    return n > 1 ? `${n} nights` : 'Tonight';
  };

  if (!stops.length) return null;

  return (
    <div data-testid="trip-ladder" className="relative">
      {navItems.length >= 2 && <DayNav items={navItems} className="-mx-3 mb-2 px-3" />}
      <p className="sr-only" aria-live="polite">
        {announce}
      </p>

      <ol ref={listRef} className="m-0 list-none p-0" aria-label="Trip stops">
        {stops.map((stop, index) => {
          const role = roleAt(stops, index);
          const letter = stopLetter(index);
          const isLast = index === stops.length - 1;
          const leg = isLast ? null : legByOrigin.get(stop.id);
          // A leg says whether it's an estimate; untagged legs follow the list.
          const legEstimate = leg?.source ? leg.source === 'estimate' : estimate;
          const nights = stop.nights ?? 0;
          const canNights = !!onChangeNights && (role === 'overnight' || role === 'via');
          const dragging = drag?.from === index;
          const showDropBefore = !!drag && drag.to === index && drag.to < drag.from;
          const showDropAfter = !!drag && drag.to === index && drag.to > drag.from;
          const dirUrl = directionsUrl(stop);
          const fixHere = showFix && !!backtrack && backtrack.fromIndex === index;
          // The day that starts at this stop (after its night), if any.
          const dayAfter = showDays && !isLast ? dayStartingAt.get(index) : undefined;
          // A day with ONE drive already says it in its header: the leg row
          // under it would repeat the same figures. Legs are written out only
          // when a day has two or more, so the header is the total and the
          // rows are the parts.
          const legDay = showDays && !isLast ? dayOfStop(days, index + 1) : undefined;
          const legSaidByHeader =
            !!legDay && legDay.lastStopIndex - legDay.firstStopIndex === 1 && typeof legDay.distanceKm === 'number';
          const badgeColor = showDays ? dayColor(stopDayIdx[index] ?? 0) : null;
          const t = times[index];
          const clock = showDays ? formatClock(index === 0 ? t?.departMinutes : t?.arriveMinutes) : null;
          const showCamp = !!onAddCamp && role === 'overnight' && typeof stop.entityId !== 'number';
          const dayTargets = showDays ? moveToDayTargets(stops, index, days) : [];
          const prevStop = index > 0 ? stops[index - 1] : null;
          const legDir = !isLast ? legDirectionsUrl(stop, stops[index + 1]) : null;

          const menuItems: MenuItem[] = [
            {
              key: 'up',
              group: 'Order',
              label: 'Move earlier',
              icon: <IconArrowUp className="h-4 w-4" />,
              disabled: index === 0 || !!stop.locked,
              onSelect: () => move(index, -1),
            },
            {
              key: 'down',
              label: 'Move later',
              icon: <IconArrowDown className="h-4 w-4" />,
              disabled: isLast || !!stop.locked,
              onSelect: () => move(index, 1),
            },
            // Onto another day's drive, just before where that day ends.
            ...dayTargets.map(
              (target, i): MenuItem => ({
                key: `day-${target.dayIndex}`,
                group: i === 0 ? 'Move to day' : undefined,
                label: `Day ${target.dayNumber}`,
                hint: `${target.fromName.split(',')[0]} → ${target.toName.split(',')[0]}`,
                icon: (
                  <span
                    aria-hidden="true"
                    className="block h-2.5 w-2.5 rounded-full"
                    style={{ background: dayColor(target.dayIndex) }}
                  />
                ),
                onSelect: () => {
                  const d = days.find((x) => x.index === target.dayIndex);
                  if (!d) return;
                  const next = moveStopToDay(stops, index, d);
                  if (next === stops) return;
                  onReorder(next);
                  setAnnounce(`${stop.name} moved to day ${target.dayNumber}, ${target.fromName} to ${target.toName}`);
                },
              })
            ),
            ...(onFindCamps && hasCoords(stop)
              ? [
                  {
                    key: 'camps',
                    group: 'This stop',
                    label: 'Find camps near here',
                    icon: <IconTent className="h-4 w-4" />,
                    onSelect: () => onFindCamps(stop),
                  },
                ]
              : []),
            ...(dirUrl
              ? [
                  {
                    key: 'dir',
                    group: onFindCamps && hasCoords(stop) ? undefined : 'This stop',
                    label: prevStop ? `Directions from ${prevStop.name.split(',')[0]}` : 'Directions',
                    hint: 'Opens Google Maps',
                    icon: <IconCompass className="h-4 w-4" />,
                    onSelect: () =>
                      window.open(
                        (prevStop && legDirectionsUrl(prevStop, stop)) || dirUrl,
                        '_blank',
                        'noopener,noreferrer'
                      ),
                  },
                ]
              : []),
            {
              key: 'note',
              group: dirUrl || (onFindCamps && hasCoords(stop)) ? undefined : 'This stop',
              label: stop.notes ? 'Edit note' : 'Add a note',
              icon: <IconNote className="h-4 w-4" />,
              onSelect: () => setEditingNoteId(stop.id),
            },
            {
              key: 'rename',
              label: 'Rename',
              icon: <IconPencil className="h-4 w-4" />,
              onSelect: () => setEditingNameId(stop.id),
            },
            {
              key: 'remove',
              label: 'Remove',
              icon: <IconX className="h-4 w-4" />,
              tone: 'danger',
              separated: true,
              disabled: !!stop.locked,
              onSelect: () => onRemoveStop(stop.id),
            },
          ];

          return (
            <li
              key={stop.id}
              ref={(el) => {
                rowRefs.current[index] = el;
                if (el) rowById.current.set(stop.id, el);
              }}
              onTouchStart={(e) => onRowTouchStart(e, index)}
              onPointerDown={(e) => onRowPointerDown(e, index)}
              onClickCapture={(e) => {
                if (suppressClick.current) {
                  e.preventDefault();
                  e.stopPropagation();
                }
              }}
              onMouseEnter={onHoverStop ? () => onHoverStop(stop.id) : undefined}
              onMouseLeave={onHoverStop ? () => onHoverStop(null) : undefined}
              data-testid="trip-stop"
              data-stop-id={stop.id}
              data-highlighted={highlightStopId === stop.id ? 'true' : undefined}
              data-selected={selectedStopId === stop.id ? 'true' : undefined}
              className={`group relative select-none rounded-tp transition-[background-color,box-shadow,transform] duration-150 ease-tp-out ${
                highlightStopId === stop.id ? 'tp-row-flash' : ''
              } ${
                dragging ? 'z-10 scale-[1.01] cursor-grabbing bg-white shadow-tp-pop' : ''
              }`}
            >
              {showDropBefore && <DropLine position="top" />}
              {showDays && index === 0 && days[0] && (
                <div data-no-drag data-sheet-row className="pb-1">
                  {renderDay(days[0])}
                </div>
              )}
              <div
                // The sheet's half detent lands between these, never through one.
                data-sheet-row
                className={`relative flex gap-3 rounded-tp py-2 pl-5 pr-0 transition-colors duration-150 ease-tp-out ${
                  selectedStopId === stop.id ? 'bg-tp-brand-soft/60' : hoveredStopId === stop.id ? 'bg-tp-surface/70' : ''
                } ${
                  dragging
                    ? ''
                    : `[@media(hover:hover)]:hover:bg-tp-surface/70 ${canDrag(index) ? '[@media(hover:hover)]:cursor-grab' : ''}`
                }`}
              >
                {/* Grip: faint at rest with a mouse (so reordering is findable),
                    full on hover; always on touch screens. */}
                {canDrag(index) && (
                  <span
                    data-grip
                    aria-hidden="true"
                    className="absolute left-0 top-1 flex h-10 w-5 cursor-grab touch-none items-center justify-center text-tp-faint opacity-40 transition-opacity duration-150 ease-tp-out group-hover:opacity-100 [@media(hover:none)]:opacity-80"
                  >
                    <IconGrip className="h-4 w-4" />
                  </span>
                )}
                {/* The timeline: one line through every dot. */}
                {stops.length > 1 && (
                  <span
                    aria-hidden="true"
                    className={`absolute left-[35px] w-0.5 bg-tp-line-strong ${
                      index === 0 ? 'bottom-0 top-[24px]' : isLast ? 'top-0 h-[24px]' : 'bottom-0 top-0'
                    }`}
                  />
                )}
                <div className="relative flex w-8 flex-none flex-col items-center">
                  {onSelectStop ? (
                    <button
                      type="button"
                      data-no-drag
                      data-testid={`trip-stop-badge-${letter}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelectStop(stop.id);
                      }}
                      aria-pressed={selectedStopId === stop.id}
                      aria-label={`Show ${letter}, ${stop.name}, on the map`}
                      title="Show on the map"
                      className={`relative z-[1] flex h-8 w-8 min-h-0 min-w-0 items-center justify-center rounded-full border-0 p-0 text-tp-sm font-bold transition-shadow duration-150 ease-tp-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tp-brand/45 focus-visible:ring-offset-2 ${
                        badgeColor ? 'text-white' : ROLE_BADGE[role]
                      } ${highlightStopId === stop.id ? 'tp-badge-pulse' : ''} ${
                        selectedStopId === stop.id ? 'shadow-[0_0_0_3px_#fff,0_0_0_5px_#1558b0]' : 'shadow-[0_0_0_3px_#fff]'
                      }`}
                      style={badgeColor ? { background: badgeColor } : undefined}
                    >
                      {letter}
                    </button>
                  ) : (
                    <span
                      className={`relative z-[1] flex h-8 w-8 items-center justify-center rounded-full text-tp-sm font-bold shadow-[0_0_0_3px_#fff] ${
                        badgeColor ? 'text-white' : ROLE_BADGE[role]
                      } ${highlightStopId === stop.id ? 'tp-badge-pulse' : ''}`}
                      style={badgeColor ? { background: badgeColor } : undefined}
                      aria-hidden="true"
                    >
                      {letter}
                    </span>
                  )}
                </div>

                <div className="min-w-0 flex-1">
                  {/* Name — its own line */}
                  {editingNameId === stop.id ? (
                    <input
                      type="text"
                      defaultValue={stop.name}
                      autoFocus
                      aria-label={`Rename ${stop.name}`}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                        if (e.key === 'Escape') setEditingNameId(null);
                      }}
                      onBlur={(e) => {
                        const v = e.target.value.trim();
                        if (v && v !== stop.name) onEditName(stop.id, v);
                        setEditingNameId(null);
                      }}
                      className={`${FIELD_CLASS} px-2.5 font-semibold`}
                      style={{ height: 32, borderRadius: 8, margin: 0 }}
                    />
                  ) : (
                    <button
                      type="button"
                      title="Rename this stop"
                      aria-keyshortcuts="Alt+ArrowUp Alt+ArrowDown"
                      onClick={() => setEditingNameId(stop.id)}
                      onKeyDown={(e) => {
                        if (!e.altKey) return;
                        if (e.key === 'ArrowUp') {
                          e.preventDefault();
                          move(index, -1);
                        } else if (e.key === 'ArrowDown') {
                          e.preventDefault();
                          move(index, 1);
                        }
                      }}
                      className="block min-h-0 max-w-full cursor-[inherit] border-0 bg-transparent p-0 pt-[5px] text-left text-tp-lg font-semibold leading-[22px] text-tp-ink [overflow-wrap:anywhere] hover:text-tp-brand focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tp-brand/45"
                    >
                      {stop.name}
                    </button>
                  )}

                  {/* Role, in one quiet style. A night adds a moon and the
                      stepper; a pass-through offers one "Add a night". */}
                  <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="inline-flex items-center gap-1 text-tp-sm text-tp-muted" data-testid="trip-role">
                      <span className="sr-only">Stop {letter}, </span>
                      {role === 'overnight' && <IconMoon className="h-3.5 w-3.5 flex-none" />}
                      {roleLabel(role)}
                      {role === 'overnight' && stop.kind === 'camp' && <span>· campsite</span>}
                    </span>
                    {canNights &&
                      (nights > 0 ? (
                        <NightsStepper
                          name={stop.name}
                          nights={nights}
                          onChange={(n) => onChangeNights!(stop.id, n)}
                        />
                      ) : (
                        <button
                          type="button"
                          data-no-drag
                          onClick={() => onChangeNights!(stop.id, 1)}
                          aria-label={`Add a night at ${stop.name}`}
                          className="-my-1 inline-flex h-7 min-h-0 items-center gap-1 rounded-full border-0 bg-transparent px-2 text-tp-sm font-medium text-tp-brand transition-colors duration-150 ease-tp-out hover:bg-tp-brand-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tp-brand/45"
                        >
                          <IconPlus className="h-3.5 w-3.5" />
                          Add a night
                        </button>
                      ))}
                    {clock && (
                      <span className="text-tp-sm tabular-nums text-tp-muted" data-testid="stop-time">
                        {index === 0 ? 'leave' : 'arrive'} {clock}
                      </span>
                    )}
                  </div>

                  {/* Note: shown when there is one, edited in place */}
                  {editingNoteId === stop.id ? (
                    <input
                      type="text"
                      defaultValue={stop.notes ?? ''}
                      autoFocus
                      placeholder="Book ahead, fuel up, swim spot…"
                      aria-label={`Note for ${stop.name}`}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                        if (e.key === 'Escape') setEditingNoteId(null);
                      }}
                      onBlur={(e) => {
                        const v = e.target.value.trim();
                        if (v !== (stop.notes ?? '')) onEditNote(stop.id, v);
                        setEditingNoteId(null);
                      }}
                      className={`${FIELD_CLASS} mt-1.5 px-2.5 text-tp-sm`}
                      style={{ height: 32, borderRadius: 8, margin: 0, marginTop: 6 }}
                    />
                  ) : (
                    stop.notes && (
                      <button
                        type="button"
                        onClick={() => setEditingNoteId(stop.id)}
                        className="mt-1 flex min-h-0 max-w-full items-start gap-1.5 border-0 bg-transparent p-0 text-left text-tp-sm text-tp-muted hover:text-tp-ink"
                      >
                        <IconNote className="mt-0.5 h-3.5 w-3.5 flex-none" />
                        <span className="[overflow-wrap:anywhere]">{stop.notes}</span>
                      </button>
                    )
                  )}
                </div>

                {/* Photo and the row's one control, inside the row's padding,
                    top-aligned with the name. */}
                <div className="flex flex-none items-start">
                  {onOpenStop ? (
                    // The photo opens the stop up close.
                    <button
                      type="button"
                      data-no-drag
                      onClick={() => onOpenStop(stop.id)}
                      aria-label={`About ${stop.name}`}
                      title={`About ${stop.name}`}
                      className="block min-h-0 min-w-0 rounded-[8px] border-0 bg-transparent p-0 transition-transform duration-150 ease-tp-out active:scale-[0.96] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tp-brand/45 focus-visible:ring-offset-2"
                      data-testid="trip-stop-photo"
                    >
                      <StopThumb url={photos ? photos[stop.id] : null} name={stop.name} badge={stopBadge(stop)} />
                    </button>
                  ) : (
                    <StopThumb url={photos ? photos[stop.id] : null} name={stop.name} badge={stopBadge(stop)} />
                  )}
                  <div className="-mr-1" data-no-drag>
                    <Menu
                      label="Options"
                      ariaLabel={`Options for ${stop.name}`}
                      iconOnly
                      size="md"
                      placement="auto"
                      icon={<IconMoreVertical className="h-5 w-5" />}
                      triggerClassName="!h-12 !w-9 !px-0 !text-tp-muted hover:!text-tp-ink"
                      items={menuItems}
                    />
                  </div>
                </div>
              </div>
              {showCamp && (
                <div className="relative pb-2 pl-[68px] pr-1" data-no-drag data-sheet-row>
                  {!isLast && <span aria-hidden="true" className="absolute bottom-0 left-[35px] top-0 w-0.5 bg-tp-line-strong" />}
                  <div
                    data-testid="stay-slot"
                    className="flex min-h-[48px] items-center gap-2.5 rounded-tp bg-tp-surface py-1.5 pl-3 pr-1.5 ring-1 ring-inset ring-tp-line"
                  >
                    <IconTent className="h-[18px] w-[18px] flex-none text-tp-muted" />
                    <p className="m-0 min-w-0 flex-1">
                      <span className="block text-tp-base font-medium text-tp-ink">No camp yet</span>
                      <span className="block text-tp-sm text-tp-muted">{stayLine(stop, index)}</span>
                    </p>
                    <button
                      type="button"
                      onClick={() => onAddCamp!(stop)}
                      data-testid="add-camp-slot"
                      aria-label={
                        (stop.nights ?? 1) > 1
                          ? `Find a camp in ${stop.name} for ${stop.nights} nights`
                          : `Find a camp in ${stop.name} for tonight`
                      }
                      className={`${dayActionBtn} text-tp-brand hover:bg-tp-brand-soft`}
                    >
                      Find a camp
                    </button>
                  </div>
                </div>
              )}
              {dayAfter && (
                <div data-no-drag data-sheet-row className="pb-1 pt-4">
                  {renderRestDays(dayAfter)}
                  {renderDay(dayAfter)}
                  <div className="relative flex min-h-[40px] items-center gap-3 pl-5 pr-1" data-testid="day-start-row">
                    <span aria-hidden="true" className="absolute bottom-0 left-[35px] top-1/2 w-0.5 bg-tp-line-strong" />
                    <span aria-hidden="true" className="relative z-[1] flex w-8 flex-none justify-center">
                      <span
                        className="h-3 w-3 rounded-full bg-white"
                        style={{ boxShadow: `inset 0 0 0 3px ${dayColor(dayAfter.index)}` }}
                      />
                    </span>
                    <p className="m-0 min-w-0 flex-1 truncate text-tp-base text-tp-muted">
                      Leave <span className="font-semibold text-tp-ink">{stop.name.split(',')[0]}</span>
                    </p>
                    <span className="flex-none pr-2 text-tp-base font-semibold tabular-nums text-tp-ink">
                      <span className="sr-only">at </span>
                      {formatClock(dayAfter.startMinutes)}
                    </span>
                  </div>
                </div>
              )}
              {!isLast && onInsertStop && insertOpenAt === index && (
                <div className="relative py-1 pl-6 pr-0 md:pl-12" data-no-drag>
                  <span aria-hidden="true" className="absolute bottom-0 left-[35px] top-0 w-0.5 bg-tp-line-strong" />
                  <AddStopBetweenPanel
                    index={index}
                    from={stop}
                    to={stops[index + 1]}
                    near={stops
                      .filter((s) => hasCoords(s))
                      .map((s) => ({ lat: s.lat as number, lng: s.lng as number }))}
                    route={route}
                    onTrip={onTripIds}
                    placement={(p) => placementInfo(stops, p, estimate ? [] : legs, index + 1)}
                    onAdd={(place, at) => {
                      setInsertOpenAt(null);
                      onInsertStop(place, at);
                    }}
                    onClose={() => setInsertOpenAt(null)}
                    extra={renderLegCamps?.(index)}
                  />
                </div>
              )}
              {!isLast && !(onInsertStop && insertOpenAt === index) && (
                <LegConnector
                  onHover={onHoverLeg ? (on) => onHoverLeg(on ? stops[index + 1]?.id ?? null : null) : undefined}
                  label={legSaidByHeader ? '' : legLabel(leg, legEstimate)}
                  nextName={stops[index + 1]?.name ?? ''}
                  estimate={legEstimate}
                  directionsUrl={legDir}
                  onAdd={onInsertStop && !drag ? () => setInsertOpenAt(index) : undefined}
                  fromName={stop.name}
                  index={index}
                >
                  {fixHere && fix && backtrack && (
                    <div
                      // Out of the leg's indent to the stop column's edge: the one-line
                      // hint gets the full row width, so it never truncates.
                      className="relative z-[1] -ml-14 flex w-[calc(100%+56px)] items-center gap-2 rounded-tp border border-solid border-tp-brand/15 bg-tp-info-soft py-1.5 pl-3 pr-1 text-tp-info animate-tp-enter motion-reduce:animate-none"
                      data-testid="trip-backtrack"
                      data-no-drag
                      title={`${stops[backtrack.fromIndex].name} to ${stops[backtrack.toIndex].name} heads ${formatKm(
                        backtrack.awayKm
                      )} away from ${stops[stops.length - 1].name}. ${
                        fix.newStart
                          ? `Starting at ${fix.newStart.name} saves about ${formatKm(fix.savedKm)}.`
                          : `A better order saves about ${formatKm(fix.savedKm)}.`
                      }`}
                    >
                      <IconRoute className="hidden h-4 w-4 flex-none sm:block" />
                      {/* One line: what, then what it saves. The name may
                          truncate; the saving never does. */}
                      <p className="m-0 flex min-w-0 flex-1 items-baseline gap-1 whitespace-nowrap text-tp-sm leading-[18px]">
                        <span className="min-w-0 truncate font-semibold">{fixHeadline(fix.newStart?.name)}</span>
                        <span className="flex-none tabular-nums text-tp-ink/70">· saves {formatKm(fix.savedKm)}</span>
                      </p>
                      {onOptimise && (
                        <Button
                          variant="filled"
                          size="sm"
                          onClick={() => onOptimise(fix.order, fix.savedKm)}
                          aria-label={`Apply: saves ${formatKm(fix.savedKm)}`}
                          className="flex-none"
                        >
                          Apply
                        </Button>
                      )}
                      <button
                        type="button"
                        onClick={() => setDismissedFix(orderKey)}
                        aria-label="Dismiss the route hint"
                        title="Dismiss"
                        className="flex h-8 w-7 min-h-0 flex-none items-center justify-center rounded-full border-0 bg-transparent text-current opacity-70 transition-opacity duration-150 hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tp-brand/45"
                      >
                        <IconX className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  )}
                </LegConnector>
              )}
              {showDropAfter && <DropLine position="bottom" />}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

/** "Start at Port Fairy" (a better start) or "A shorter order" (a better middle). */
export function fixHeadline(newStartName?: string | null): string {
  return newStartName ? `Start at ${newStartName}` : 'A shorter order';
}

/**
 * The drive between two stops, written beside the line between their dots,
 * so it reads as the leg. Plain text, not a pill: it isn't a button.
 */
function LegConnector({
  label,
  nextName,
  estimate,
  onAdd,
  fromName,
  index,
  directionsUrl: dirUrl,
  onHover,
  children,
}: {
  /** Pointer entered / left the leg: the map lights it up. */
  onHover?: (on: boolean) => void;
  label: string;
  nextName: string;
  estimate: boolean;
  /** Google Maps directions for this drive. */
  directionsUrl?: string | null;
  /** Present → a "+" on the timeline opens "add a stop here". */
  onAdd?: () => void;
  fromName?: string;
  index?: number;
  children?: React.ReactNode;
}) {
  // Pointing at the "+" (or keyboard focus on it) swaps the drive figure for
  // the action it offers. Tracked from real pointer movement, not :hover:
  // after an insert the list moves under a still cursor, and :hover left the
  // seam stuck saying "Add a stop here" instead of the new leg's drive.
  const [pointing, setPointing] = useState(false);
  const [keyFocus, setKeyFocus] = useState(false);
  const lit = !!onAdd && (pointing || keyFocus);
  const hasContent = !!label || !!children || !!onAdd;
  return (
    <div
      className={`relative flex flex-col items-start gap-1.5 pl-16 pr-1 ${
        onAdd ? 'min-h-[36px] justify-center py-0.5 [@media(hover:none)]:min-h-[44px]' : hasContent ? 'py-0.5' : 'h-2'
      }`}
      data-testid={onAdd ? `add-stop-seam-${index}` : undefined}
      onMouseEnter={onHover ? () => onHover(true) : undefined}
      onMouseLeave={onHover ? () => onHover(false) : undefined}
    >
      <span aria-hidden="true" className="absolute bottom-0 left-[35px] top-0 w-0.5 bg-tp-line-strong" />
      {onAdd && (
        <button
          type="button"
          data-no-drag
          data-testid={`add-stop-between-${index}`}
          aria-label={`Add a stop between ${fromName} and ${nextName}`}
          title={`Add a stop between ${fromName} and ${nextName}`}
          onPointerMove={(e) => {
            if (e.pointerType === 'mouse' && !pointing) setPointing(true);
          }}
          onPointerLeave={() => setPointing(false)}
          onFocus={(e) => setKeyFocus(e.currentTarget.matches(':focus-visible'))}
          onBlur={() => setKeyFocus(false)}
          onClick={(e) => {
            e.stopPropagation();
            setPointing(false);
            setKeyFocus(false);
            onAdd();
          }}
          className="group/add absolute left-[14px] top-[18px] z-[1] flex h-11 w-11 min-h-0 min-w-0 -translate-y-1/2 items-center justify-center rounded-full border-0 bg-transparent p-0 text-tp-brand focus-visible:outline-none md:left-[18px] md:h-9 md:w-9 [@media(hover:none)]:top-[22px]"
        >
          <span
            className={`flex h-6 w-6 flex-none items-center justify-center rounded-full border-[1.5px] border-solid border-tp-brand transition-[background-color,color,transform] duration-150 ease-tp-out group-active/add:scale-95 ${
              lit ? 'scale-110 bg-tp-brand text-white' : 'bg-white'
            } ${keyFocus ? 'ring-2 ring-tp-brand/30 ring-offset-1' : ''}`}
          >
            <IconPlus className="h-3.5 w-3.5" />
          </span>
        </button>
      )}
      {(label || onAdd) && (
        <div className="relative flex h-6 items-center">
          {label && (
            <p
              className={`relative m-0 inline-flex h-6 items-center gap-1.5 whitespace-nowrap text-tp-sm tabular-nums text-tp-muted transition-opacity duration-150 ${
                lit ? 'opacity-0' : 'opacity-100'
              }`}
              data-testid="trip-leg"
              title={estimate ? 'Straight-line distance; drive time estimated' : `Drive to ${nextName}`}
            >
              <IconCar className="h-4 w-4 flex-none text-tp-faint" />
              <span className="sr-only">Drive to {nextName}: </span>
              <span>{label}</span>
            </p>
          )}
          {label && dirUrl && !lit && (
            <a
              href={dirUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-no-drag
              onClick={(e) => e.stopPropagation()}
              title="Directions in Google Maps"
              aria-label={`Directions from ${fromName} to ${nextName} in Google Maps (opens in a new tab)`}
              className="relative ml-1 hidden h-6 min-h-0 items-center gap-1 rounded-full px-1.5 text-tp-xs font-medium text-tp-brand no-underline transition-colors duration-150 ease-tp-out hover:bg-tp-brand-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tp-brand/45 sm:inline-flex"
            >
              <IconCompass className="h-3.5 w-3.5" />
              Directions
            </a>
          )}
          {lit && (
            <span aria-hidden="true" className="absolute left-0 whitespace-nowrap text-tp-sm font-medium text-tp-brand animate-tp-fade motion-reduce:animate-none">
              Add a stop here
            </span>
          )}
        </div>
      )}
      {children && <div className="w-full">{children}</div>}
    </div>
  );
}

/**
 * 48px stop photo (with a kind badge). A tinted tile with a map pin is always
 * underneath; the photo fades in over it once it has loaded. So a slow, lazy
 * or missing photo is a calm illustration, never a blank grey box.
 */
function StopThumb({ url, name, badge }: { url: string | null | undefined; name: string; badge?: string | null }) {
  const [failed, setFailed] = useState(false);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    setFailed(false);
    setLoaded(false);
  }, [url]);
  const box = 'h-12 w-12 flex-none overflow-hidden rounded-[8px]';
  const inner = (
    <span
      className={`${box} relative flex items-center justify-center bg-[linear-gradient(150deg,#e8f0fb_0%,#e2efe6_100%)] text-tp-brand/45 ring-1 ring-inset ring-tp-line`}
      aria-hidden="true"
    >
      <IconPin className={`h-5 w-5 ${url === undefined ? 'animate-tp-pulse motion-reduce:animate-none' : ''}`} />
      {url && !failed && (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={url}
          alt=""
          title={/wikipedia|wikimedia/.test(url) ? `${name} · photo via Wikimedia Commons` : undefined}
          loading="lazy"
          draggable={false}
          ref={(el) => {
            // A cached photo can finish before React attaches onLoad.
            if (el?.complete && el.naturalWidth > 0 && !loaded) setLoaded(true);
          }}
          onLoad={() => setLoaded(true)}
          onError={() => setFailed(true)}
          className={`absolute inset-0 block h-full w-full object-cover transition-opacity duration-220 ease-tp-out motion-reduce:transition-none ${
            loaded ? 'opacity-100' : 'opacity-0'
          }`}
        />
      )}
    </span>
  );
  return (
    <span className="relative block flex-none">
      {inner}
      {badge && (
        <span className="absolute bottom-0.5 left-0.5 rounded-full bg-white/95 px-1 text-[10px] font-semibold leading-[14px] text-tp-ink shadow-[0_1px_2px_rgba(22,35,58,0.2)]">
          {badge}
        </span>
      )}
    </span>
  );
}

function DropLine({ position }: { position: 'top' | 'bottom' }) {
  return (
    <span
      aria-hidden="true"
      className={`pointer-events-none absolute left-1 right-0 z-20 flex items-center ${position === 'top' ? '-top-[2px]' : '-bottom-[2px]'}`}
    >
      <span className="h-2.5 w-2.5 rounded-full border-2 border-solid border-tp-brand bg-white" />
      <span className="h-0.5 flex-1 rounded-full bg-tp-brand" />
    </span>
  );
}

function NightsStepper({ name, nights, onChange }: { name: string; nights: number; onChange: (n: number) => void }) {
  const btn =
    'flex h-8 w-8 min-h-0 items-center justify-center border-0 bg-transparent text-tp-ink transition-colors duration-150 ' +
    'hover:bg-tp-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-tp-brand/45 ' +
    'disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent';
  return (
    <span data-no-drag className="inline-flex items-center overflow-hidden rounded-full border border-solid border-tp-line-strong bg-white">
      <button
        type="button"
        aria-label={`One less night at ${name}`}
        disabled={nights <= 0}
        onClick={() => onChange(nights - 1)}
        className={btn}
      >
        <IconMinus className="h-3.5 w-3.5" />
      </button>
      <span className="min-w-[64px] px-1 text-center text-tp-sm font-medium tabular-nums text-tp-ink" aria-live="polite">
        {nights === 0 ? 'No night' : `${nights} night${nights === 1 ? '' : 's'}`}
      </span>
      <button
        type="button"
        aria-label={`One more night at ${name}`}
        disabled={nights >= MAX_NIGHTS_PER_STOP}
        onClick={() => onChange(nights + 1)}
        className={btn}
      >
        <IconPlus className="h-3.5 w-3.5" />
      </button>
    </span>
  );
}
